import React, { useState, useEffect } from 'react';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';

// Animated Counter Component
const AnimatedNumber = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    const incrementTime = duration / end;
    
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
};

// Circular Score Gauge Component
const ScoreGauge = ({ score, label, theme }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedScore(score), 500);
    return () => clearTimeout(timer);
  }, [score]);

  const getScoreColor = (s) => {
    if (s >= 70) return '#00D4AA';
    if (s >= 50) return '#FFB800';
    return '#FF4757';
  };

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      transition={{ type: 'spring', stiffness: 300 }}
      style={{
        position: 'relative',
        width: '140px',
        height: '140px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx="70"
          cy="70"
          r="45"
          fill="none"
          stroke={theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
          strokeWidth="8"
        />
        {/* Animated progress circle */}
        <motion.circle
          cx="70"
          cy="70"
          r="45"
          fill="none"
          stroke={getScoreColor(score)}
          strokeWidth="8"
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          style={{
            strokeDasharray: circumference,
            filter: `drop-shadow(0 0 8px ${getScoreColor(score)})`
          }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: label ? '24px' : '36px',
          fontWeight: 700,
          fontFamily: "'Sansation', sans-serif",
          color: getScoreColor(score),
          marginTop: label ? '-8px' : '0',
        }}>
          <AnimatedNumber value={score} />
        </div>
        <div style={{
          fontSize: '8px',
          fontWeight: 700,
          letterSpacing: '0.15em',
          opacity: 0.7,
          marginTop: '2px'
        }}>
          {label}
        </div>
      </div>
    </motion.div>
  );
};

// Bar Chart Component
const BarChart = ({ data, theme }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', height: '120px', padding: '0 8px' }}>
      {data.map((item, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: `${item.value}%` }}
          transition={{ delay: i * 0.1, duration: 0.8, ease: 'easeOut' }}
          whileHover={{ 
            scale: 1.05,
            filter: 'brightness(1.2)'
          }}
          style={{
            flex: 1,
            background: `linear-gradient(180deg, ${C.secondary} 0%, ${C.primary} 100%)`,
            borderRadius: '4px 4px 0 0',
            minHeight: '10px',
            cursor: 'pointer',
            position: 'relative',
            boxShadow: `0 0 10px ${C.secondary}40`
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            style={{
              position: 'absolute',
              top: '-24px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
              color: theme === 'dark' ? '#000' : '#fff',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {item.value}%
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subtext, theme, color, isMobile }) => (
  <motion.div
    whileHover={{ 
      y: isMobile ? 0 : -4,
      boxShadow: `0 8px 24px ${C.secondary}20`
    }}
    transition={{ type: 'spring', stiffness: 400 }}
    style={{
      background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      borderRadius: '12px',
      padding: isMobile ? '12px' : '16px',
      cursor: 'pointer'
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
      <span style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{icon}</span>
    </div>
    <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, fontFamily: "'Sansation', sans-serif", color: color || 'inherit' }}>
      {value}
    </div>
    <div style={{ fontSize: isMobile ? '9px' : '10px', opacity: 0.5, marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtext}</div>
  </motion.div>
);

// Main Dashboard Mockup Component
export const DashboardMockup = ({ type = 'dashboard' }) => {
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const dimensions = [
    { name: 'Credibility', shortName: 'Cred', value: 72 },
    { name: 'Clarity', shortName: 'Clarity', value: 85 },
    { name: 'Market Fit', shortName: 'Market', value: 68 },
    { name: 'Edge', shortName: 'Edge', value: 78 },
    { name: 'Presentation', shortName: 'Present', value: 65 }
  ];

  const bgColor = theme === 'dark' ? '#0D1117' : '#FFFFFF';
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const textColor = theme === 'dark' ? '#FFFFFF' : '#1A1A2E';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      whileHover={{ scale: isMobile ? 1 : 1.02 }}
      style={{
        background: bgColor,
        borderRadius: '20px',
        border: `1px solid ${borderColor}`,
        padding: isMobile ? '16px' : '24px',
        boxShadow: theme === 'dark' 
          ? '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,130,251,0.1)' 
          : '0 20px 60px rgba(0,0,0,0.1)',
        color: textColor,
        maxWidth: '600px',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}
      data-testid="dashboard-mockup"
    >
      {/* Glow effect */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle, ${C.secondary}15 0%, transparent 70%)`,
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? '16px' : '20px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '10px', height: '10px', borderRadius: '50%', 
            background: '#FF5F56' 
          }} />
          <div style={{ 
            width: '10px', height: '10px', borderRadius: '50%', 
            background: '#FFBD2E' 
          }} />
          <div style={{ 
            width: '10px', height: '10px', borderRadius: '50%', 
            background: '#27CA40' 
          }} />
        </div>
        <div style={{
          fontSize: isMobile ? '10px' : '12px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: C.secondary
        }}>
          PLATFORM DASHBOARD
        </div>
      </div>

      {/* Main Content Grid - Responsive */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '140px 1fr',
        gap: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '16px' : '20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Score Gauge */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'row' : 'column', 
          alignItems: 'center',
          justifyContent: isMobile ? 'space-around' : 'flex-start',
          gap: isMobile ? '16px' : '0'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? '9px' : '10px', 
              fontWeight: 700, 
              letterSpacing: '0.15em', 
              color: C.secondary, 
              marginBottom: '8px' 
            }}>
              DECX SCORE™
            </div>
            <ScoreGauge score={72} theme={theme} />
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            style={{
              marginTop: isMobile ? '0' : '12px',
              padding: '6px 12px',
              background: 'linear-gradient(90deg, #00D4AA20, #00D4AA40)',
              borderRadius: '100px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#00D4AA',
              border: '1px solid #00D4AA40',
              whiteSpace: 'nowrap'
            }}
          >
            ✓ Decision Ready
          </motion.div>
        </div>

        {/* Bar Chart */}
        <div>
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            fontWeight: 700, 
            marginBottom: '4px',
            fontFamily: "'Sansation', sans-serif"
          }}>
            DecX AI Score™ Breakdown
          </div>
          <div style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.5, marginBottom: isMobile ? '12px' : '16px' }}>
            TechStartup_Pitch.pdf
          </div>
          <BarChart data={dimensions} theme={theme} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '8px',
            fontSize: isMobile ? '7px' : '9px',
            opacity: 0.6,
            gap: '4px'
          }}>
            {dimensions.map((d, i) => (
              <span key={i} style={{ 
                textAlign: 'center', 
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {isMobile ? d.shortName : d.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Row - Responsive Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '20px',
        position: 'relative',
        zIndex: 1
      }}>
        <StatCard 
          icon="📊" 
          label="Slides" 
          value="14" 
          subtext="Deck slides"
          theme={theme}
          isMobile={isMobile}
        />
        <StatCard 
          icon="⚡" 
          label="Actions" 
          value="3" 
          subtext="Priority fixes"
          theme={theme}
          color="#FFB800"
          isMobile={isMobile}
        />
        <StatCard 
          icon="📈" 
          label="Avg Score" 
          value="72" 
          subtext="All audits"
          theme={theme}
          color="#00D4AA"
          isMobile={isMobile}
        />
        {!isMobile && (
          <StatCard 
            icon="🎯" 
            label="Market Fit" 
            value="Strong" 
            subtext="Industry avg: 65"
            theme={theme}
            color={C.secondary}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* Reports Table - Hidden on very small screens */}
      {!isMobile && (
        <div style={{
          background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          borderRadius: '12px',
          padding: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ 
            fontSize: '13px', 
            fontWeight: 700, 
            marginBottom: '12px',
            fontFamily: "'Sansation', sans-serif"
          }}>
            My Audit Reports
          </div>
          
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
            gap: '8px',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            opacity: 0.5,
            padding: '8px 0',
            borderBottom: `1px solid ${borderColor}`
          }}>
            <span>DECK NAME</span>
            <span>DATE</span>
            <span>TYPE</span>
            <span>STATUS</span>
            <span>SCORE</span>
          </div>

          {/* Table Rows */}
          {[
            { name: 'TechStartup_Pitch.pdf', date: '28 Feb 2026', type: 'Paid', status: 'Completed', score: 72 },
            { name: 'SaaS_Sales_Deck.pdf', date: '25 Feb 2026', type: 'Paid', status: 'Completed', score: 85 }
          ].map((row, i) => (
            <motion.div
              key={i}
              whileHover={{ 
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                x: 4
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                gap: '8px',
                fontSize: '11px',
                padding: '12px 0',
                borderBottom: `1px solid ${borderColor}`,
                cursor: 'pointer',
                borderRadius: '4px',
                alignItems: 'center'
              }}
            >
              <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
              <span style={{ opacity: 0.7 }}>{row.date}</span>
              <span>
                <span style={{
                  padding: '2px 8px',
                  background: `${C.secondary}20`,
                  color: C.secondary,
                  borderRadius: '100px',
                  fontSize: '9px',
                  fontWeight: 700
                }}>
                  ✦ {row.type}
                </span>
              </span>
              <span>
                <span style={{
                  padding: '2px 8px',
                  background: '#00D4AA20',
                  color: '#00D4AA',
                  borderRadius: '100px',
                  fontSize: '9px',
                  fontWeight: 700
                }}>
                  ● {row.status}
                </span>
              </span>
              <span style={{ 
                fontWeight: 700, 
                fontSize: '16px',
                color: row.score >= 70 ? '#00D4AA' : row.score >= 50 ? '#FFB800' : '#FF4757'
              }}>
                {row.score}<span style={{ fontSize: '10px', opacity: 0.5 }}>/100</span>
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Legacy MockupGraphic for other use cases
export const MockupGraphic = ({ type = 'dashboard', label }) => {
  const { theme } = useTheme();
  const bgColor = theme === 'dark' ? '#0D1117' : '#FFFFFF';
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

  if (type === 'dashboard') {
    return <DashboardMockup />;
  }

  const renderContent = () => {
    switch (type) {
      case 'score':
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ScoreGauge score={85} label="YOUR SCORE" theme={theme} />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              style={{ marginTop: '20px', fontSize: '14px', color: '#00D4AA', fontWeight: 600 }}
            >
              Decision Ready - Go get that yes!
            </motion.p>
          </div>
        );

      case 'chart':
        return (
          <div style={{ padding: '30px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '20px', fontFamily: "'Sansation', sans-serif" }}>
              {label || 'Score Breakdown'}
            </div>
            <BarChart 
              data={[
                { name: 'Credibility', value: 75 },
                { name: 'Clarity', value: 82 },
                { name: 'Market', value: 68 },
                { name: 'Edge', value: 71 },
                { name: 'Presentation', value: 79 }
              ]} 
              theme={theme} 
            />
          </div>
        );

      case 'artefacts':
        return (
          <div style={{ padding: '30px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '20px', fontFamily: "'Sansation', sans-serif" }}>
              {label || 'Ready Artefacts'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['Pitch Canvas', 'Outreach Sequence', 'Brand Video Script'].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.2 }}
                  whileHover={{ x: 8, background: `${C.secondary}15` }}
                  style={{
                    padding: '12px 16px',
                    background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    border: `1px solid ${borderColor}`
                  }}
                >
                  <span style={{ color: C.secondary }}>✓</span>
                  <span style={{ fontSize: '13px' }}>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        );

      default:
        return <DashboardMockup />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.5 }}
      style={{
        background: bgColor,
        borderRadius: '20px',
        border: `1px solid ${borderColor}`,
        boxShadow: theme === 'dark' 
          ? '0 20px 60px rgba(0,0,0,0.5), 0 0 30px rgba(0,130,251,0.1)' 
          : '0 20px 60px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        position: 'relative'
      }}
      data-testid={`mockup-${type}`}
    >
      {/* Window controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '16px 20px',
        borderBottom: `1px solid ${borderColor}`
      }}>
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#FF5F56' }} />
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#FFBD2E' }} />
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#27CA40' }} />
        <span style={{ 
          marginLeft: 'auto', 
          fontSize: '11px', 
          fontWeight: 700, 
          letterSpacing: '0.1em',
          color: C.secondary 
        }}>
          {label || type.toUpperCase()}
        </span>
      </div>
      {renderContent()}
    </motion.div>
  );
};
